import {
  da,
  de,
  deAT,
  enGB,
  enUS,
  es,
  fi,
  fr,
  frCH,
  hu,
  it,
  itCH,
  nl,
  pl,
  pt,
  ptBR,
  vi
} from 'date-fns/locale';
import { parse, parseISO } from 'date-fns';
import { DateFormatter } from './DateFormatter';

// NOTE: any new locales need to be added here!
const locales = {
  'de-AT': deAT,
  'en-GB': enGB,
  'en-US': enUS,
  'fr-CH': frCH,
  'it-CH': itCH,
  'pt-BR': ptBR,
  da,
  de,
  en: enUS,
  es,
  fi,
  fr,
  hu,
  it,
  nl,
  pl,
  pt,
  vi
};

const currentLocale = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  let locale = locales[window.SenseConstants?.locale ?? ''];
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!locale) locale = locales[window.SenseConstants?.language ?? ''];
  return locale ?? locales['en'];
};

/**
 * (more or less) safely parse a user-input date.
 *
 * This will attempt to parse it like {@link DefaultDateFormatter} would format it, in the users' locale.
 * ISO strings will also work, in order to support Dates from AJAX calls.
 * Already-parsed Date objects are passed through.
 *
 * @param input date as string or object
 * @author Bennett Piater
 */
export function parseDate(input: Date | string): Date {
  // Dates are passed through
  if (typeof input === 'object') {
    return input;
  }

  // always be capable of parsing ISO strings
  const parsedAsISO = parseISO(input);
  if (!isNaN(parsedAsISO.valueOf())) return parsedAsISO;

  // Parse in UTC to have 0 UTC hours. This *should* work fine with our (de)serializtion, but flatpickr and yup need to be checked.
  return parse(input + 'Z', 'PX', new Date(), { locale: currentLocale() });
}

/**
 * Detect based upon an input (e.g 1, 01, J, Jan., January) what Month formatting was used.
 * https://date-fns.org/v2.29.3/docs/parse
 *
 * 1 = M
 * 01 = MM
 * J = MMMMM
 * Jan. = MMM
 * January = MMMM
 *
 * Not supported
 * 1st = Mo
 * @param value a month as a string
 */
function detectFormatMonth(value: string): string {
  const length = value.length;
  switch (length) {
    case 1:
      if (!Number.isNaN(value)) {
        return 'MMMMM';
      } else {
        return 'M';
      }
    case 2:
      return 'MM';
    //3 and 4 are combined since in german the dot is part of the month
    case 3:
    case 4:
      return 'MMM';
    default:
      return 'MMMM';
  }
}

/**
 * Get a Date formatting string (eg. dd/MM/YYYY, HH:mm) based on {@link Intl.DateTimeFormat} options.
 * Supported:
 * month: numeric, 2-digit, long, short, narrow
 * day: numeric, 2-digit
 * year: numeric, 2-digit
 * hour: numeric, 2-digit
 * minute: numeric, 2-digit
 * second: numeric, 2-digit
 *
 * DISCLAIMER!
 * Does not support every possible setting!!
 * Time should not have hourCycle h11, h12, since AM, PM parsing is not correctly implemented. (You would get a warning that type dayPeriod is not supported).
 *
 * @param options the options.
 */
export function getDateFormattingString(options: Intl.DateTimeFormat): string {
  const elements = options.formatToParts(new Date(1, 1, 1, 1, 1, 1, 1));
  const result = new Array<string>(elements.length);
  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];
    switch (el.type) {
      case 'month':
        result[i] = detectFormatMonth(el.value);
        break;
      case 'day':
        result[i] = 'd'.repeat(el.value.length);
        break;
      case 'year':
        result[i] = 'y'.repeat(el.value.length);
        break;
      case 'hour':
        result[i] = 'H'.repeat(el.value.length);
        break;
      case 'minute':
        result[i] = 'm'.repeat(el.value.length);
        break;
      case 'second':
        result[i] = 's'.repeat(el.value.length);
        break;
      case 'literal':
        result[i] = el.value;
        break;
      default:
        console.warn('Not supported DateTime formatting part: %s', el.type);
        break;
    }
  }

  return result.join('');
}

/**
 * Takes a string and a makes a date formatting string out of it (e.g. 01/01/2000, 11:00 => dd/MM/yyyy, HH:mm).
 * First it always tries to parse UTC.
 * If you do not provide a date formatter the date constructor will be used for parsing
 * In case of inability to parse something now will be returned as fallback.
 *
 * @param date the date
 * @param options the formatting options used to create the string provided as the first parameter
 */
export function parseDateWithOptions(
  date: string,
  options?: DateFormatter
): Date {
  try {
    // always be capable of parsing ISO strings
    // The datepicker on mobile phones formats everything by himself and then returns UTC conform date string
    console.debug('Date input: %s', date);
    const parsedAsISO = parseISO(date);

    if (!isNaN(parsedAsISO.valueOf())) return parsedAsISO;

    if (!options) {
      return new Date(date);
    }

    const dateFormattingString = getDateFormattingString(
      new Intl.DateTimeFormat(options.locale, options.options)
    );
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const loc = locales[options.locale] ?? enGB;
    console.log(
      'The value %s will be parsed with %s. Locale %s',
      date,
      dateFormattingString,
      loc.code
    );
    const result =
      parse(date, dateFormattingString, new Date(), {
        locale: loc
      }) ?? new Date();
    console.debug('The date formatting string is: %s', result);
    if (Number.isNaN(result.getUTCDate())) {
      console.debug('The date could not be parsed return now as fallback');
      return new Date();
    } else {
      return result;
    }
  } catch (e: unknown) {
    console.error('Parsing a date yielded an error: %s', e);
    return new Date();
  }
}
